import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../common/layout/header';
import Footer from '../common/layout/footer';
import Sidebar from '../common/sidebar';
import { BaseUrl } from '../common/config';
import { http } from '../helper';
import CommonAlert from '../common/Commonalert';
import ShowAlert from '../common/Showalert';
import Select from 'react-select';
import { canAction } from '../common/permissionhelper';

const MODULE = 'manage_masterentry';

const AccountcodeEdit = () => {
  const { id } = useParams(); // ID of the record to edit
  const navigate = useNavigate();

  const canView = canAction(MODULE, 'view');
  const canEdit = canAction(MODULE, 'edit');
  const isReadOnly = !canEdit;

  const [accountCodeOptions, setAccountCodeOptions] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState('');
  const [originalCode, setOriginalCode] = useState('');
  const [restrictedCodeSet, setRestrictedCodeSet] = useState(new Set());
  const [inputValue, setInputValue] = useState('');
  const [errMessage, setErrMessage] = useState('');
  const [alertPopup, setAlertPopup] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [errorPopup, setErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

    if(!sessionStorage.getItem("isLoggedIn")){
    window.location.href = '/';
  }
  if(JSON.parse(sessionStorage.getItem('authData')) != '' && !JSON.parse(sessionStorage.getItem('authData')).adminRole.permissions.includes('manage_masterentry'))
    {
      window.location.href = '/unauthorized';
    }

  // Redirect if no view permission
  useEffect(() => {
    if (!canView) navigate('/restrictedaccountcode/accountcode');
  }, [canView]);

  // Fetch all account codes & restricted codes
  useEffect(() => {
    const fetchCodes = async () => {
      try {
        const [allRes, restrictedRes] = await Promise.all([
          http.get(`${BaseUrl}admin/account`),
          http.get(`${BaseUrl}admin/accountcodelist`)
        ]);

        const allCodes = allRes.data.data || [];
        const restrictedCodes = restrictedRes.data.data || [];
        const restrictedSet = new Set(restrictedCodes.map(item => item.accountCode?.toLowerCase()));
        setRestrictedCodeSet(restrictedSet);
        // show ALL account codes in dropdown
        setAccountCodeOptions(allCodes);
      } catch (err) {
        console.log(err);
      }
    };
    fetchCodes();
  }, []);

  // Fetch existing account codes for editing
  useEffect(() => {
    const fetchExisting = async () => {
        try {
            const res = await http.get(`${BaseUrl}admin/accountcode/${id}`);
            if (res.data.success && res.data.data) {
              // Wrap single accountCode in an array
              const existingCode = res.data.data.accountCode || '';
              setSelectedCodes(existingCode);
              setOriginalCode(existingCode);
            } else {
                setErrorPopup(true);
                setErrorMessage('Failed to fetch account code details.');
            }
        } catch (err) {
            setErrorPopup(true);
            setErrorMessage('Something went wrong while fetching data.');
            console.log(err);
        }
    };
    if (id) fetchExisting();
}, [id]);


  const handleCancel = () => navigate('/restrictedaccountcode/accountcode');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInputValue('');

    if (isReadOnly) return;

    if (!selectedCodes || selectedCodes.trim() === '') {
      setErrMessage('Please select an account code.');
      return;
    }
    const newCodeLower = selectedCodes.trim().toLowerCase();
    const originalLower = (originalCode || '').trim().toLowerCase();
    // If the newly selected code is restricted and is different from original, show error
    if (restrictedCodeSet.has(newCodeLower) && newCodeLower !== originalLower) {
      // Show popup and STOP the update
      setErrorPopup(true);
      setErrorMessage(`Account code "${selectedCodes}" is already restricted.`);
      return;
    }

    const payload = { id, selectedAccountCodes: [selectedCodes] }; 

    try {
      const res = await http.put(`${BaseUrl}admin/accountcode`, payload);
      if (res.data.success) {
        setAlertPopup(true);
        setAlertMessage('Account Code updated successfully');
        setTimeout(() => navigate('/restrictedaccountcode/accountcode'), 1500);
      } else {
        setErrorPopup(true);
        setErrorMessage('Failed to update account code(s)');
      }
    } catch (err) {
      console.log(err);
      const backendMsg = err?.response?.data?.message || 'Something went wrong while updating.';
      setErrorPopup(true);
      setErrorMessage(backendMsg);
    }
  };

  const customNoOptionsMessage = () => {
    const input = inputValue.trim().toLowerCase();
    if (restrictedCodeSet.has(input)) return null;
    if (input.length > 0) return 'No options';
    return null;
  };

  return (
    <>
      {errorPopup && <ShowAlert title='Alert' message={errorMessage} />}
      {alertPopup && <CommonAlert title='Success' message={alertMessage} />}
      <div className="dashboard-block">
        <Sidebar />
        <div className="dash-left">
          <Header />
          <section className="cart-block">
            <div className="container">
              <h1 className="checkout-title">{canEdit ? 'Edit Account Code' : 'View Account Code'}</h1>
              <div className="right-table-outer-wrap">
                <form className="addform form-container-xs" onSubmit={handleSubmit}>
                  <div className='input-field col-1'>
                    <label>Account Code</label>
                    <Select
                      isMulti={false}
                      closeMenuOnSelect={true}
                      noOptionsMessage={customNoOptionsMessage}
                      // Select expects an object {value,label} for single select
                      value={selectedCodes ? { value: selectedCodes, label: selectedCodes } : null}
                      onChange={(selectedOption) => {
                        const code = selectedOption ? selectedOption.value : '';
                        setSelectedCodes(code);
                        setInputValue('');
                        if (code) setErrMessage('');
                      }}
                      onInputChange={(value, { action }) => {
                        if (action === 'input-change') {
                          setInputValue(value);
                          if (restrictedCodeSet.has(value.trim().toLowerCase())) {
                            setErrMessage(`Account code "${value}" is already restricted`);
                            setTimeout(() => {
                              setErrMessage('');
                              setInputValue('');
                            }, 1000);
                          }
                        }
                      }}
                      onBlur={() => setInputValue('')}
                      inputValue={inputValue}
                      options={accountCodeOptions.map(opt => ({
                        value: opt.accountCode,
                        label: opt.accountCode
                      }))}
                      className="form-control"
                      placeholder="Select Account Code"
                      styles={{ menu: base => ({ ...base, position: 'relative', zIndex: 2 }) }}
                      isDisabled={isReadOnly}
                    />
                    {errMessage && <div className="error-msg">{errMessage}</div>}
                  </div>
                  {!isReadOnly && <button type="submit">Update</button>}&nbsp;&nbsp;
                  <button type="button" onClick={handleCancel}>Cancel</button>
                </form>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default AccountcodeEdit;
